import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { useScrollReveal } from "../lib/useScrollReveal";
import Seo from "../components/Seo";
import { MarketingNav, MarketingFooter, FaqItem, BillingToggle, L } from "../components/marketing/MarketingChrome";
import { PLANS, priceLabel, advisoryLabel, annualSaving, ANNUAL_DISCOUNT_PCT, LIFETIME_DISCOUNT_PCT, LIFETIME_CAP, lifetimeBefore } from "../lib/plans";
import { useBilling } from "../lib/useBilling";
import { Check, Minus, ArrowRight, Sparkles, Flame } from "lucide-react";

const num = (v, lang) => v.toLocaleString(lang === "fr" ? "fr-FR" : "en-US");
const fmtCredits = (v, lang) => (v === "custom" ? L(lang, "Sur mesure", "Custom") : num(v, lang));
const fmtUnlimited = (v, lang) => (v === null ? L(lang, "Illimité", "Unlimited") : v);
const fmtCollaborators = (v, lang) => (v === null ? L(lang, "Illimités", "Unlimited") : v === 0 ? L(lang, "Solo", "Solo") : v);
const fmtAutomations = (v, lang) => (v === null ? L(lang, "Illimitées", "Unlimited") : v);
const fmtStorage = (v, lang) => {
  if (v === null) return L(lang, "Sur mesure", "Custom");
  if (v < 1) return `${v * 1000} ${L(lang, "Mo", "MB")}`;
  if (v >= 1000) return `${v / 1000} ${L(lang, "To", "TB")}`;
  return `${v} ${L(lang, "Go", "GB")}`;
};

export default function Pricing() {
  const { lang, user } = useApp();
  const navigate = useNavigate();
  const { startCheckout } = useBilling();
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState("");
  const [ltStatus, setLtStatus] = useState(null);
  useEffect(() => {
    api.get("/billing/lifetime-status").then(({ data }) => setLtStatus(data)).catch(() => {});
  }, []);
  const ltSoldOut = ltStatus && !ltStatus.available;
  const rootRef = useRef(null);
  useScrollReveal(rootRef, lang);
  const onPick = async (plan) => {
    if (plan.id === "enterprise") { window.location.href = "mailto:hello@cockpit-os.com?subject=Cockpit%20OS%20Enterprise"; return; }
    if (plan.id === "free") { navigate(user ? "/" : "/login?signup=1"); return; }
    if (!user) { navigate("/login?signup=1"); return; }
    // Logged-in user upgrading their own workspace → Stripe Checkout.
    setBusy(plan.id);
    try { await startCheckout(plan.id, cycle); } catch { setBusy(""); }
  };

  const rows = [
    { label: L(lang, "Entités / espaces de travail", "Entities / workspaces"), get: (p) => fmtUnlimited(p.limits.entities, lang) },
    { label: L(lang, "Espaces (spaces)", "Spaces"), get: (p) => fmtUnlimited(p.limits.spaces, lang) },
    { label: L(lang, "Collaborateurs inclus", "Collaborators included"), get: (p) => fmtCollaborators(p.limits.collaborators, lang) },
    { label: L(lang, "Lecteurs (lecture seule)", "Viewers (read-only)"), get: () => L(lang, "Illimités · gratuits", "Unlimited · free") },
    { label: L(lang, "Conseil IA", "AI Advisory"), get: (p) => advisoryLabel(p.limits.advisory, lang) },
    { label: L(lang, "Crédits IA / mois", "AI credits / month"), get: (p) => fmtCredits(p.limits.aiCredits, lang), mono: true },
    { label: L(lang, "Tarifs préférentiels Anthropic & derniers modèles", "Preferential Anthropic rates & latest models"), bool: () => true },
    { label: L(lang, "Vos propres clés IA (OpenAI · Anthropic · Gemini)", "Bring your own AI keys (OpenAI · Anthropic · Gemini)"), bool: () => true },
    { label: L(lang, "Automatisations", "Automations"), get: (p) => fmtAutomations(p.limits.automations, lang) },
    { label: L(lang, "Stockage", "Storage"), get: (p) => fmtStorage(p.limits.storageGb, lang) },
    { label: L(lang, "Gestion du travail (Tableaux, Projets, OKR)", "Work management (Boards, Projects, OKRs)"), bool: () => true },
    { label: L(lang, "Finances, actifs & patrimoine", "Money, assets & net worth"), bool: () => true },
    { label: L(lang, "CRM & pipeline", "CRM & pipeline"), bool: () => true },
    { label: L(lang, "Permissions avancées", "Advanced permissions"), bool: (p) => p.limits.advancedPermissions },
    { label: L(lang, "Onboarding accompagné", "Guided onboarding"), bool: (p) => p.limits.onboarding },
    { label: L(lang, "LDAP / SSO", "LDAP / SSO"), bool: (p) => p.id === "enterprise" },
    { label: L(lang, "Déploiement privé / managé", "Private / managed deployment"), bool: (p) => p.id === "enterprise" },
  ];

  const faqs = [
    { q: L(lang, "Y a-t-il un essai gratuit ?", "Is there a free trial?"),
      a: L(lang, "Oui. Vous pouvez démarrer immédiatement sur le plan Gratuit, ou profiter d'un essai gratuit de 7 jours du plan Business (toutes les fonctionnalités), sans carte bancaire. À la fin de l'essai, votre compte bascule automatiquement sur le plan Gratuit : toutes vos données restent accessibles et vous souscrivez quand vous le souhaitez.",
                 "Yes. You can start right away on the Free plan, or take a 7-day free trial of the Business plan (all features), no credit card required. When the trial ends, your account automatically moves to the Free plan: all your data stays accessible and you can subscribe whenever you want.") },
    { q: L(lang, "Quelle différence entre revues et board meetings ?", "What's the difference between reviews and board meetings?"),
      a: L(lang, "Les revues (plans Pro et plus) évaluent vos performances sur une période. Les board meetings (plans Business et plus) sont des sessions interactives où les conseillers IA challengent une décision précise.",
                 "Reviews (Pro and up) assess your performance over a period. Board meetings (Business and up) are interactive sessions where AI advisors challenge a specific decision.") },
    { q: L(lang, "La facturation est-elle au siège (par utilisateur) ?", "Is billing per seat (per user)?"),
      a: L(lang, "Non. Chaque plan inclut un nombre fixe de collaborateurs (membres, managers, admins) — Solo 3, Pro 30, Business 100, Portfolio illimité. Pour ajouter plus d'utilisateurs, vous passez simplement au plan supérieur : aucun surcoût par siège. Les lecteurs en lecture seule sont toujours gratuits et illimités. La facturation est par espace de travail : le propriétaire paie pour son espace.",
                 "No. Each plan includes a fixed number of collaborators (members, managers, admins) — Solo 3, Pro 30, Business 100, Portfolio unlimited. To add more users you simply upgrade to a higher plan: no per-seat surcharge. Read-only viewers are always free and unlimited. Billing is per workspace: the owner pays for their workspace.") },
    { q: L(lang, "Mensuel ou annuel ?", "Monthly or annual?"),
      a: L(lang, `Les deux. En facturation annuelle, vous économisez environ ${ANNUAL_DISCOUNT_PCT} % par rapport au mensuel.`,
                 `Both. On annual billing you save about ${ANNUAL_DISCOUNT_PCT}% versus paying monthly.`) },
    { q: L(lang, "Qu'est-ce que l'offre À vie ?", "What is the Lifetime offer?"),
      a: L(lang, `Offre de lancement limitée aux ${LIFETIME_CAP} premiers clients : payez une seule fois (−${LIFETIME_DISCOUNT_PCT}%) et conservez votre plan (Pro, Business ou Portfolio) à vie, sans abonnement. Vos crédits IA se renouvellent chaque mois selon votre plan.`,
                 `Launch offer limited to the first ${LIFETIME_CAP} customers: pay once (−${LIFETIME_DISCOUNT_PCT}%) and keep your plan (Pro, Business or Portfolio) for life — no subscription. Your AI credits still reset every month per your plan.`) },
    { q: L(lang, "Comment fonctionnent les crédits IA ?", "How do AI credits work?"),
      a: L(lang, "Chaque génération IA (revue, board meeting, résumé) consomme des crédits, renouvelés chaque mois selon votre plan. Ce sont un compteur d'usage, pas la valeur principale du produit.",
                 "Each AI generation (review, board meeting, summary) consumes credits, renewed monthly by plan. They're a usage meter, not the product's main value.") },
    { q: L(lang, "Puis-je utiliser mes propres clés IA ?", "Can I use my own AI keys?"),
      a: L(lang, "Oui. Nous proposons des tarifs préférentiels sur les tokens Anthropic et les derniers modèles, inclus dans votre plan. Vous pouvez aussi connecter vos propres clés OpenAI, Anthropic ou Gemini depuis les réglages : l'usage devient illimité et facturé directement par votre fournisseur, sans décompte de crédits.",
                 "Yes. We offer preferential rates on Anthropic tokens and the latest models, included in your plan. You can also connect your own OpenAI, Anthropic or Gemini keys from settings: usage becomes unlimited and billed directly by your provider, with no credits deducted.") },
    { q: L(lang, "Puis-je annuler à tout moment ?", "Can I cancel anytime?"),
      a: L(lang, "Oui. Changez ou annulez votre plan à tout moment depuis le portail de facturation. Aucun engagement.",
                 "Yes. Change or cancel your plan anytime from the billing portal. No commitment.") },
  ];

  return (
    <div ref={rootRef} className="force-light bg-white text-slate-900 min-h-screen" data-testid="pricing-page">
      <Seo path="/pricing"
        title={L(lang, "Tarifs - Cockpit OS", "Pricing - Cockpit OS")}
        description={L(lang, "Un plan gratuit pour toujours, puis Pro a 49 EUR/mois. Sans minimum de sieges, lecteurs illimites et gratuits, conseil IA. Essai Business 7 jours sans carte.", "A free-forever plan, then Pro from EUR 49/mo. No seat minimums, unlimited free viewers, AI advisory. 7-day Business trial, no card.")} />
      <MarketingNav />

      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-8 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600 mb-5">
          <Sparkles className="w-3.5 h-3.5 text-blue-600" /> {L(lang, "EUR · TVA en sus", "EUR · VAT excluded")}
        </div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter text-slate-900 mb-4">
          {L(lang, "Commencez simple, ", "Start simple, ")}<span className="text-blue-600">{L(lang, "évoluez quand il le faut", "scale when needed")}</span>
        </h1>
        <p className="text-slate-600 text-lg max-w-xl mx-auto mb-8">
          {L(lang, "Des plans pensés par profondeur opérationnelle.",
                   "Plans built around operating depth.")}
        </p>
        <div className="flex justify-center"><BillingToggle cycle={cycle} onChange={setCycle} lang={lang} /></div>
        <p className="text-center text-sm text-slate-500 mt-4" data-testid="pricing-trial-note">
          {L(lang, "Essai gratuit de 7 jours sur tous les plans — aucune carte bancaire requise pour commencer.", "7-day free trial on every plan — no credit card required to start.")}
        </p>
      </section>

      {/* Tier cards */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-16">
        {cycle === "lifetime" && (
          <div className={`max-w-3xl mx-auto mb-6 rounded-xl border px-4 py-3 flex items-center justify-center gap-2 text-sm ${ltSoldOut ? "border-slate-300 bg-slate-50 text-slate-600" : "border-amber-300 bg-amber-50 text-amber-800"}`} data-testid="lifetime-urgency">
            <Flame className={`w-4 h-4 shrink-0 ${ltSoldOut ? "text-slate-400" : "text-amber-500"}`} />
            {ltSoldOut ? (
              <span className="font-semibold">{L(lang, "Offre a vie de lancement epuisee — merci !", "Lifetime launch offer sold out — thank you!")}</span>
            ) : (
              <span>
                <span className="font-bold">{L(lang, "Offre de lancement a vie", "Lifetime launch offer")}</span>
                {" \u00b7 "}{L(lang, `reservee aux ${LIFETIME_CAP} premiers`, `limited to the first ${LIFETIME_CAP}`)}
                {ltStatus ? <> {" \u00b7 "}<span className="font-bold tabular-nums">{L(lang, `${ltStatus.remaining} restantes`, `${ltStatus.remaining} left`)}</span></> : null}
              </span>
            )}
          </div>
        )}
        <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 items-stretch ${cycle === "lifetime" ? "lg:grid-cols-3 max-w-4xl mx-auto" : "lg:grid-cols-5"}`}>
          {(cycle === "lifetime" ? PLANS.filter((p) => p.priceLifetime) : PLANS).map((p, i) => {
            const saving = cycle === "annual" ? annualSaving(p) : 0;
            return (
              <div key={p.id} data-testid={`plan-card-${p.id}`}
                className={`rounded-2xl border p-4 flex flex-col bg-white animate-fade-up transition-all ${p.popular ? "border-blue-600 shadow-lg shadow-blue-600/10 lg:scale-[1.04]" : "border-slate-200 shadow-sm hover:-translate-y-1 hover:shadow-md"}`}
                style={{ animationDelay: `${i * 70}ms` }}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-heading font-bold text-lg text-slate-900">{p.name[lang]}</div>
                  {p.popular && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white bg-blue-600 rounded-full px-2.5 py-1 whitespace-nowrap">
                      {L(lang, "Le + choisi", "Most popular")}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1 mb-4 min-h-[48px]">{p.framing[lang]}</p>
                <div className="mb-1 flex items-baseline gap-2 flex-wrap">
                  {cycle === "lifetime" && p.priceLifetime && (
                    <span className="text-slate-400 text-base line-through" data-testid={`plan-lifetime-before-${p.id}`}>€{lifetimeBefore(p)}</span>
                  )}
                  <span className="font-heading font-black text-3xl tracking-tighter text-slate-900">{priceLabel(p, lang, cycle)}</span>
                  {p.price > 0 && (
                    <span className="text-slate-500 text-sm">
                      {cycle === "lifetime" && p.priceLifetime ? L(lang, "à vie", "lifetime") : `/${L(lang, "mois", "mo")}`}
                    </span>
                  )}
                  {cycle === "lifetime" && p.priceLifetime && (
                    <span className="text-[10px] font-bold text-white bg-amber-500 rounded-full px-2 py-0.5">−{LIFETIME_DISCOUNT_PCT}%</span>
                  )}
                </div>
                {cycle === "lifetime" && p.priceLifetime ? (
                  <div className="text-[11px] text-amber-600 font-semibold -mt-0.5 mb-1" data-testid={`plan-lifetime-note-${p.id}`}>
                    {L(lang, "Paiement unique · accès à vie", "One-time payment · lifetime access")}
                    {p.price > 0 && (
                      <span className="block text-emerald-600 font-semibold" data-testid={`plan-lifetime-payback-${p.id}`}>
                        {L(lang, `Rentabilisée en ~${Math.round(p.priceLifetime / p.price)} mois vs mensuel`,
                                 `Pays for itself in ~${Math.round(p.priceLifetime / p.price)} months vs monthly`)}
                      </span>
                    )}
                  </div>
                ) : cycle === "annual" && p.price > 0 && p.priceAnnual > 0 ? (
                  <div className="text-[11px] text-slate-500 -mt-0.5 mb-1" data-testid={`plan-annual-total-${p.id}`}>
                    {L(lang, `soit €${p.priceAnnual} facturés par an`, `€${p.priceAnnual} billed annually`)}
                  </div>
                ) : null}
                <div className="h-4 mb-3 text-[11px] text-emerald-600 font-semibold">
                  {cycle === "annual" && saving > 0 ? L(lang, `Économisez €${saving}/an`, `Save €${saving}/yr`) : ""}
                </div>
                <button onClick={() => onPick(p)} disabled={busy === p.id || (cycle === "lifetime" && p.priceLifetime && ltSoldOut)} data-testid={`plan-cta-${p.id}`}
                  className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors mb-5 ${p.popular ? "bg-blue-600 text-white hover:bg-blue-700" : "border border-slate-300 text-slate-900 hover:bg-slate-50"} ${busy === p.id || (cycle === "lifetime" && p.priceLifetime && ltSoldOut) ? "opacity-60 cursor-not-allowed" : ""}`}>
                  {cycle === "lifetime" && p.priceLifetime && ltSoldOut ? L(lang, "Offre épuisée", "Sold out")
                    : busy === p.id ? L(lang, "Redirection…", "Redirecting…")
                    : p.id === "free" ? L(lang, "Commencer gratuitement", "Start free")
                    : p.id === "enterprise" ? p.cta[lang]
                    : cycle === "lifetime" ? (user ? L(lang, "Acheter à vie", "Get lifetime") : L(lang, "Créer un compte", "Create account"))
                    : (!user) ? L(lang, "Démarrer l'essai gratuit", "Start free trial")
                    : p.cta[lang]}
                </button>
                <ul className="space-y-2">
                  {p.features[lang].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[13px] text-slate-700">
                      <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-500" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* Honest comparison strip vs per-seat tools (monday.com) — just before the comparison table */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-4" data-testid="pricing-vs-monday">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-5">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-700">
            {[
              L(lang, "Plan gratuit, pour toujours", "Free forever plan"),
              L(lang, "Aucun minimum de 3 sièges", "No 3-seat minimum"),
              L(lang, "Lecteurs illimités et gratuits", "Unlimited free viewers"),
              L(lang, "Plans à prix fixe, sans surcoût par siège", "Flat-rate plans, no per-seat fees"),
            ].map((txt) => (
              <span key={txt} className="inline-flex items-center gap-1.5 font-medium"><Check className="w-4 h-4 text-emerald-500" />{txt}</span>
            ))}
          </div>
          <p className="text-center text-xs text-slate-500 mt-3">
            {L(lang, "Les outils par siège facturent souvent dès 3 sièges minimum (~27 €/mois). Cockpit OS démarre gratuitement, avec des plans à prix fixe.",
                     "Per-seat tools often bill from a 3-seat minimum (~€27/mo). Cockpit OS starts free, with flat-rate plans.")}
          </p>
        </div>
      </section>

      {/* Comparison table */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-20" data-testid="pricing-comparison">
        <h2 className="font-heading font-bold text-2xl tracking-tight text-slate-900 mb-6">{L(lang, "Comparer les plans", "Compare plans")}</h2>
        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full text-sm min-w-[1000px]">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left font-medium text-slate-500 px-4 py-3 w-1/4">{L(lang, "Fonctionnalité", "Feature")}</th>
                {PLANS.map((p) => (
                  <th key={p.id} className={`px-3 py-3 text-center font-heading font-semibold ${p.popular ? "text-blue-600" : "text-slate-900"}`}>{p.name[lang]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.label} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3 text-slate-600">{r.label}</td>
                  {PLANS.map((p) => (
                    <td key={p.id} className={`px-3 py-3 text-center ${r.mono ? "font-mono tabular-nums text-slate-900" : "text-slate-900"} ${p.popular ? "bg-blue-50/50" : ""}`}>
                      {r.bool ? (
                        r.bool(p) ? <Check className="w-4 h-4 mx-auto text-emerald-500" /> : <Minus className="w-4 h-4 mx-auto text-slate-300" />
                      ) : r.get(p)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Billing FAQ */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-20">
        <h2 className="font-heading font-bold text-2xl tracking-tight text-slate-900 mb-6">{L(lang, "Questions sur la facturation", "Billing FAQ")}</h2>
        <div>{faqs.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} testid={`pricing-faq-${i}`} />)}</div>
      </section>

      {/* CTA (solid blue band) */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-24">
        <div className="rounded-3xl bg-blue-600 p-10 sm:p-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.12),transparent_45%)]" />
          <div className="relative">
            <h2 className="font-heading font-black text-3xl tracking-tight text-white mb-4">{L(lang, "Construisez votre cockpit aujourd'hui", "Build your cockpit today")}</h2>
            <button onClick={() => navigate(user ? "/" : "/login?signup=1")} data-testid="pricing-final-cta"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-white text-blue-600 font-semibold hover:bg-blue-50 transition-colors shadow-lg">
              {L(lang, "Démarrer l'essai gratuit", "Start free trial")} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
